import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import classNames from 'classnames';
import {BoxProps, useMediaQuery} from '@chakra-ui/react';
import {UiBox, UiButton, UiCenter, UiContainer, UiIcon, UiSimpleGrid, UiText} from '~/components/elements';
import {IoIosArrowBack} from 'react-icons/all';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '~/redux/root-reducer';
// import {setBackQuestionnaire, setNextQuestionnaire} from '~/redux/questionnaires/slice';
import {AppDispatch} from '~/redux/root-store';
import {ELEVATE_LINK, METHOD_KEYS, PAGE_PATHS, PAGE_URLS, PROGRESS_DATA, QUERY_MOBILE} from '~/constants';
// import {Action, AnswerData, MedicalQuestionnaireKey} from '~/redux/questionnaires/types';
import {UiImage} from '~/components/elements/UiImage';
import {Row} from '~/components/elements/grid/Row';
import {Col} from '~/components/elements/grid/Col';
import {getCurrentUserFromStorage} from '~/services/auth';
import {Action, MDQ2Params, MedicalQuestionnaireKey} from '~/types/models/questionnaire';
import {redirectInIframe, triggerChat} from '~/utils/helpers';
import placeholderAvatar from '~/assets/images/elevate_logo.png';
import {
  setBackAnswerOrder,
  setBackQuestionnaire,
  setMedicalUUID,
  setMethodKey,
  setNextQuestionnaire,
} from '~/redux/questionnaires/slice';
import {useHistory} from 'react-router-dom';
import {reportToSentry} from '~/utils/error-handler';
import {useRequestState} from '~/hooks';
import {getMdqStatus, submitOrLogMdq} from '~/redux/questionnaires/thunk';
import {requestOK} from 'redux-thunk-kit';
import {unwrapResult} from '@reduxjs/toolkit';
const queryString = require('query-string');

interface Props extends BoxProps {
  isActive?: boolean;
  backIcon?: boolean;
  children: React.ReactNode;
  containerClasses?: string;
  handleOnNext?: () => void;
  handleGoBack?: () => void;
  addProductOnNext?: (data: any) => void;
  buttonTittle?: string;
  disableNext?: boolean;
  nextScreenKey?: MedicalQuestionnaireKey;
  checkoutStep?: number;
  actions?: Action[];
  loading?: boolean;
  isShowLogin?: boolean;
  hasFloatActionButtons?: boolean;
  showHeader?: boolean;
  showBackButton?: boolean;
  pageTitle?: string;
  isFirstScreen?: boolean;
}

export const UiQuestionnaireLayout = memo(function UiQuestionnaireLayout({
  containerClasses,
  children,
  backIcon,
  handleOnNext,
  handleGoBack,
  addProductOnNext,
  buttonTittle = 'Continue',
  disableNext = false,
  nextScreenKey,
  checkoutStep,
  actions,
  loading = false,
  isShowLogin,
  hasFloatActionButtons = true,
  showHeader = true,
  showBackButton = true,
  pageTitle,
  isFirstScreen = false,
  ...rest
}: Props) {
  const [isDesktop] = useMediaQuery(`(min-width: ${QUERY_MOBILE})`);
  const dispatch: AppDispatch = useDispatch();
  const history = useHistory();
  const activeQuestion = useSelector((state: RootState) => state.questionnaires.activeQuestion);
  const isWebview = useSelector((state: RootState) => state.questionnaires.isWebview);
  const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);
  const {wpAuthUser} = medicalQuestionnaire;
  const {accessToken: savedToken} = getCurrentUserFromStorage();
  const [redirectLoading, setRedirectLoading] = React.useState(false);
  const medicalUUID = useSelector((state: RootState) => state.questionnaires.medicalUUID);
  const {loading: trackMDQLoading} = useRequestState(submitOrLogMdq);
  const collectedData = useSelector((state: RootState) => state.questionnaires.collectedData);

  const onClickLogin = () => {
    setRedirectLoading(true);
    if (wpAuthUser?.baseURL && wpAuthUser?.currentURL) {
      redirectInIframe(wpAuthUser.baseURL + '/login?redirect_to=' + wpAuthUser?.currentURL, '_self');
    }
  };

  const renderActionButtons = () => {
    return (
      <>
        {actions?.map(item => {
          return (
            <UiButton
              key={item.value}
              disabled={disableNext || redirectLoading || loading}
              pointerEvents={disableNext || redirectLoading || loading ? 'none' : 'auto'}
              onClick={() => nextScreen(item)}
              title={item.label}
              minW="9.375rem"
              variant={item.value === 'EXIT' || item.value === 'EXIT_INVALID' ? 'secondary' : 'primary'}
              bg={item.value === 'NEXT_NO_ADD' ? '#F1EEE4' : undefined}
              isLoading={loading}
              alignItems="center"
              justifyContent="center"
              display="flex"
              mx={2}
              mb={2}
              _hover={
                isDesktop
                  ? {
                      bg: 'trueBlack',
                      color: 'whiteText',
                      _disabled: {
                        bg: '#F2F2F2',
                        opacity: 1,
                        color: 'telOnly',
                      },
                    }
                  : {}
              }
            />
          );
        })}
      </>
    );
  };

  const renderActionContainer = (isShowFloatBtn: boolean) => {
    if (isShowFloatBtn) {
      return (
        <Col
          width={{base: '100%', md: 'auto'}}
          style={{position: 'fixed', bottom: 10, left: '50%', transform: 'translateX(-50%)', justifyContent: 'center'}}
        >
          {renderActionButtons()}
        </Col>
      );
    }
    return (
      <UiSimpleGrid columns={1} spacing={actions && actions?.length > 1 ? 5 : 0} width={{base: '100%', md: 'auto'}}>
        {renderActionButtons()}
        {isShowLogin && (
          <UiButton
            onClick={onClickLogin}
            title="Login / Sign up"
            minW="150px"
            _hover={{
              background: 'baseColor',
              color: 'white',
            }}
            alignItems="center"
            justifyContent="center"
            display="flex"
            mt={2}
            isLoading={redirectLoading}
          />
        )}
      </UiSimpleGrid>
    );
  };

  const nextScreen = async (item: Action) => {
    // item.userAction.params
    switch (item.userAction.value) {
      case 'NEXT':
      case 'FORM_SUBMIT_AND_NEXT':
      case 'NEXT_NO_ADD':
        if (nextScreenKey) {
          dispatch(setNextQuestionnaire({value: nextScreenKey}));
        }
        if (handleOnNext) {
          handleOnNext();
          // dispatch(
          //   trackMedicalQuestionnaireScreen({
          //     data: {
          //       methodKey,
          //       mdqKey: mdqId,
          //       screen: activeQuestion.keys[activeQuestion.keys.length - 2],
          //       wpAuthUser,
          //       collectedData: finalcollectedData,
          //     },
          //   }),
          // );
        }
        break;
      case 'NEXT_ADD':
        if (addProductOnNext) {
          addProductOnNext(item?.userAction?.params);
        }
        break;
      case 'NAVIGATE_TO':
        window.location.href = item?.userAction?.params
          ? `/?methodKey=${item.userAction.params}&token=${savedToken}${isWebview ? '&isWebview=true' : ''}`
          : window.location.href;
        break;
      // case 'EXIT':
      //   if (isWebview) {
      //     //   window.open(TELEHEALTH_DEEPLINK, '_self');
      //     // } else {
      //     redirectInIframe(ELEVATE_LINK, '_blank');
      //   }
      //   break;
      case 'RETURN_TO_WEBSITE':
      case 'EXIT_INVALID':
      case 'EXIT':
        redirectInIframe(ELEVATE_LINK, '_blank');
        break;
      case 'SHOW_CHAT':
      case 'SPEAK_WITH_CARE_TEAM':
        // if (isWebview) {
        //   window.open(SPEAK_TO_CARE_TEAM_DEEPLINK, '_self');
        // } else {
        triggerChat();
        // }
        break;
      case 'RETURN_TO_PROGRAM_SELECTION':
        history.push(PAGE_URLS.MDQ1_PROGRAM_SELECTION);
        break;
      default:
        // eslint-disable-next-line no-console
        console.warn('Not implement yet!');
        break;
    }
  };

  const prevScreen = () => {
    if (handleGoBack) {
      handleGoBack();
    }
    history.goBack();
    dispatch(setBackQuestionnaire({}));
    dispatch(setBackAnswerOrder({}));
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (pageTitle) {
      document.title = pageTitle;
    }
  }, [pageTitle]);

  useEffect(() => {
    const currentScreen = window.location.pathname;
    if (currentScreen.includes(PAGE_PATHS.MDQ_2) || currentScreen.includes(PAGE_PATHS.MDQ_RENEWAL)) {
      return;
    }
    if (!medicalUUID && !isFirstScreen && !trackMDQLoading) {
      console.log({
        medicalUUID,
        isFirstScreen,
        trackMDQLoading,
        currentScreen,
      });
      console.log('collectedData', collectedData);
      if (Object.keys(collectedData || {}).length > 0) {
        const error = new Error('medicalUUID is Null');
        reportToSentry(error);
      }
      history.replace(PAGE_URLS.MDQ1_HOME);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, isFirstScreen, medicalUUID, trackMDQLoading]);

  useEffect(() => {
    const fetchData = async () => {
      const location = window.location;
      const pathname = location.pathname;
      if (!pathname.includes(PAGE_PATHS.MDQ_2)) {
        return;
      }
      if (
        !pathname.includes(PAGE_PATHS.MDQ_2_INVALID) &&
        pathname !== PAGE_PATHS.MDQ_2 &&
        !medicalUUID &&
        !pathname.includes(PAGE_PATHS.MDQ_2_10)
      ) {
        history.push(PAGE_URLS.MDQ2_HOME + location.search);
      }
    };
    fetchData();
  }, [dispatch, history, medicalUUID]);

  useEffect(() => {
    const checkData = async () => {
      const location = window.location;
      const pathname = location.pathname;
      if (!pathname.includes(PAGE_PATHS.MDQ_RENEWAL)) {
        return;
      }
      if (!pathname.includes(PAGE_PATHS.MDQ_RENEWAL_INVALID) && pathname !== PAGE_PATHS.MDQ_RENEWAL && !medicalUUID) {
        history.push(PAGE_URLS.MDQ_RENEWAL_HOME + location.search);
      }
    };
    checkData();
  }, [dispatch, history, medicalUUID]);

  return (
    <UiContainer
      maxW="500px"
      height="100vh"
      position="relative"
      p="20px"
      pt="5px"
      pb={0}
      display="flex"
      flexDirection="column"
      style={{flex: '1 0 auto', WebkitBoxFlex: 1, display: '-webkit-flex'}}
      {...rest}
    >
      {showHeader && (
        <UiCenter mt={6}>
          {showBackButton && (
            <UiIcon
              as={IoIosArrowBack}
              boxSize={5}
              onClick={prevScreen}
              color="black"
              cursor="pointer"
              position="absolute"
              left={5}
            />
          )}
          <UiImage url={placeholderAvatar} height="2rem" width="6.25rem" />
        </UiCenter>
      )}
      <Col flex={1}>
        {checkoutStep && (
          <Row justifyContent="center" alignItems="center" mt={3.5}>
            {PROGRESS_DATA.map(i => (
              <UiText
                textDecoration={checkoutStep === i.value ? 'underline' : 'none'}
                textAlign="center"
                fontWeight="700"
                fontSize="xs"
                key={i.value}
                mx={{base: '8%', md: 20}}
              >
                {i.label}
              </UiText>
            ))}
          </Row>
        )}

        <Col
          id="qns-layout"
          className={classNames(`animate__animated animate__fadeInLeft`, containerClasses)}
          pb="20px"
          flex={1}
          mb="5%"
          // flexGrow={1}
        >
          {children}
        </Col>
        <UiBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          pt={0}
          pb={{base: hasFloatActionButtons ? 32 : 8, lg: 8}}
          w="100%"
        >
          {renderActionContainer(hasFloatActionButtons)}
        </UiBox>
      </Col>
    </UiContainer>
  );
});
